import React, { useEffect, useState } from "react";
import { Slider, Typography } from "@mhome/ui";
import { SyncBadge } from "../../common/badges";
import { kelvinToRGB, getDisplayColor } from "../common/color-util";

const ColorDetail1Widget = ({ widget, onValueUpdate, pattern }) => {
  const [colorRgb, setColorRgb] = useState("ffffff");
  const [colorTemp, setColorTemp] = useState(2700);
  const [current, setCurrent] = useState("rgb");
  const [widgetData, setWidgetData] = useState(null);
  const [tempMinMax, setTempMinMax] = useState({ min: 2700, max: 6500 });

  // Color picker state for RGB
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);

  // Track if user is actively dragging sliders to prevent state override
  const isDraggingRef = React.useRef(false);
  const draggingTypeRef = React.useRef(null); // 'hue', 'saturation', 'temperature', or null

  // Track last values to prevent unnecessary updates
  const lastColorRgbRef = React.useRef(null);
  const lastColorTempRef = React.useRef(null);

  // Determine widget mode based on available items
  const getWidgetMode = () => {
    if (!widgetData) return "all";

    const hasRgb = !!widgetData.items?.$w1;
    const hasTemp = !!widgetData.items?.$w2;
    const hasMode = !!widgetData.items?.$w3;

    if (hasRgb && hasTemp && hasMode) {
      return "all"; // Full color widget with both RGB and temperature
    } else if (hasRgb && !hasTemp) {
      return "rgb"; // RGB-only widget
    } else if (hasTemp && !hasRgb) {
      return "temperature"; // Temperature-only widget
    } else {
      return "all"; // Fallback
    }
  };

  // Initialize widget data (only when widget object changes)
  useEffect(() => {
    if (!widget) return;
    setWidgetData(widget);

    if (widget.items?.$w2) {
      setTempMinMax({
        min: widget.items.$w2.min ?? 2700,
        max: widget.items.$w2.max ?? 6500,
      });
    }

    if (widget.items?.$w3) {
      setCurrent(widget.items.$w3.value || "rgb");
    } else {
      // If no mode selector, determine current based on available data
      if (widget.items?.$w1 && !widget.items?.$w2) {
        setCurrent("rgb");
      } else if (widget.items?.$w2 && !widget.items?.$w1) {
        setCurrent("temperature");
      }
    }

    // Initialize last values from widget
    if (widget.items?.$w1) {
      const colorValue = widget.items.$w1.value.toLowerCase() || "ffffff";
      const newColorRgb = colorValue.replace(/^#/, "");
      lastColorRgbRef.current = newColorRgb;
      setColorRgb(newColorRgb);
    }

    if (widget.items?.$w2) {
      const newTemp = Number(widget.items.$w2.value) || 2700;
      lastColorTempRef.current = newTemp;
      setColorTemp(newTemp);
    }
  }, [widget]);

  // Update RGB color from widget (only when widget value actually changes from external source)
  useEffect(() => {
    if (!widget?.items?.$w1) return;

    const colorValue = widget.items.$w1.value.toLowerCase() || "ffffff";
    const newColorRgb = colorValue.replace(/^#/, "");

    // Only update if color actually changed and it's not from user interaction
    // Compare with last value to avoid unnecessary updates
    if (newColorRgb !== lastColorRgbRef.current) {
      lastColorRgbRef.current = newColorRgb;
      setColorRgb(newColorRgb);
    }
  }, [widget?.items?.$w1?.value]);

  // Update hue and saturation sliders based on colorRgb state (like original code)
  useEffect(() => {
    if (!colorRgb) return;

    // Skip if user is actively dragging hue or saturation
    if (
      draggingTypeRef.current === "hue" ||
      draggingTypeRef.current === "saturation"
    ) {
      return;
    }

    // Convert hex to HSL
    const hex = colorRgb.startsWith("#") ? colorRgb.substring(1) : colorRgb;
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    setHue(Math.round(h * 360));
    setSaturation(Math.round(s * 100));
  }, [colorRgb]); // Depend on colorRgb state, like original code

  // Update temperature from widget (only when widget value actually changes from external source)
  useEffect(() => {
    if (!widget?.items?.$w2) return;

    const newTemp = Number(widget.items.$w2.value) || 2700;
    // Only update if value actually changed
    if (newTemp !== lastColorTempRef.current) {
      lastColorTempRef.current = newTemp;
      setColorTemp(newTemp);
    }
  }, [widget?.items?.$w2?.value]);

  const handleValueUpdate = (data, type) => {
    if (!widgetData) return;
    const widgetMode = getWidgetMode();

    if (widgetMode === "rgb") {
      // RGB-only mode
      const updatedWidget = {
        ...widgetData.items.$w1,
        value: data.replace(/^#/, ""),
      };
      onValueUpdate([updatedWidget]);
    } else if (widgetMode === "temperature") {
      // Temperature-only mode
      const updatedWidget = {
        ...widgetData.items.$w2,
        value: data.toString(),
      };
      onValueUpdate(updatedWidget);
    } else {
      // Full mode with both RGB and temperature
      const updatedWidgets = {};

      if (type === "$w1") {
        updatedWidgets.$w1 = { ...widgetData.items.$w1 };
        updatedWidgets.$w3 = { ...widgetData.items.$w3 };
        updatedWidgets.$w1.value = data.replace(/^#/, "");
        updatedWidgets.$w3.value = "rgb";
        setCurrent("rgb");
      } else if (type === "$w2") {
        updatedWidgets.$w2 = { ...widgetData.items.$w2 };
        updatedWidgets.$w3 = { ...widgetData.items.$w3 };
        updatedWidgets.$w2.value = data;
        updatedWidgets.$w3.value = "temperature";
        setCurrent("temperature");
      }

      onValueUpdate(Object.values(updatedWidgets));
    }
  };

  // RGB Color functions
  const updateRgbColor = (newHue, newSaturation) => {
    if (pattern && pattern.endsWith("_R")) return;

    // Convert HSL to Hex
    const h = newHue / 360;
    const s = newSaturation / 100;
    const l = 0.5;

    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (x) => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };

    const hexColor = `${toHex(r)}${toHex(g)}${toHex(b)}`;
    // Update ref immediately to prevent useEffect from reverting
    lastColorRgbRef.current = hexColor;
    setColorRgb(hexColor);
    handleValueUpdate(hexColor, "$w1");
  };

  const handleHueChange = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    draggingTypeRef.current = "hue";
    setHue(newValue);
  };

  const handleHueChangeCommitted = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    updateRgbColor(newValue, saturation);
    // Clear drag state immediately after update
    draggingTypeRef.current = null;
  };

  const handleSaturationChange = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    draggingTypeRef.current = "saturation";
    setSaturation(newValue);
  };

  const handleSaturationChangeCommitted = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    updateRgbColor(hue, newValue);
    // Clear drag state immediately after update
    draggingTypeRef.current = null;
  };

  // Temperature functions
  const handleTemperatureChange = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    draggingTypeRef.current = "temperature";
    setColorTemp(newValue);
  };

  const handleTemperatureChangeCommitted = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    // Update state immediately (like original code)
    setColorTemp(newValue);
    lastColorTempRef.current = newValue;
    handleValueUpdate(newValue, "$w2");
    // Clear drag state immediately after update
    draggingTypeRef.current = null;
  };

  const getCurrentColor = () => {
    const widgetMode = getWidgetMode();
    const mode =
      widgetMode === "rgb" || current === "rgb" ? "rgb" : "temperature";

    return getDisplayColor(colorRgb || "ffffff", colorTemp, mode);
  };

  const disabled = pattern && pattern.endsWith("_R");
  const widgetMode = getWidgetMode();

  // Generate temperature gradient
  const gradientStops = [];
  const steps = 10;
  for (let i = 0; i <= steps; i++) {
    const temp =
      tempMinMax.min + (i / steps) * (tempMinMax.max - tempMinMax.min);
    const rgb = kelvinToRGB(temp);
    gradientStops.push(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
  }

  const sliderStyles = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: "100%",
    left: 0,
    right: 0,
    zIndex: 1,
    "& .MuiSlider-thumb": {
      width: 4,
      height: 20,
      borderRadius: 16,
      backgroundColor: "#fff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
      "&:hover, &.Mui-active": {
        boxShadow: "0 1px 4px rgba(0,0,0,0.6)",
      },
      "&:before": {
        display: "none",
      },
    },
    "& .MuiSlider-track": {
      height: 25,
      border: "none",
      borderRadius: 16,
      backgroundColor: "transparent",
    },
    "& .MuiSlider-rail": {
      height: 25,
      borderRadius: 16,
      backgroundColor: "transparent",
    },
  };

  const isRgbActive = current === "rgb";
  const isTempActive = current === "temperature";
  const showHue = widgetMode === "all" || widgetMode === "rgb";
  const showSaturation = widgetMode === "all" || widgetMode === "rgb";
  const showTemperature = widgetMode === "all" || widgetMode === "temperature";

  if (!widgetData) {
    return (
      <div className="h-full flex items-center justify-center">
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Sync Badge */}
      <div className="flex items-center justify-between">
        <SyncBadge
          widget={widgetData}
          onValueUpdate={onValueUpdate}
          onStateUpdate={() => {}}
          onTriggerEvent={() => {}}
        />
      </div>

      {/* Color Controls */}
      <div
        className="flex flex-col rounded-2xl"
        style={{
          padding: "12px",
          backgroundColor: "hsl(var(--card))",
        }}
      >
        {/* RGB Controls */}
        {showHue && (
          <div className="mb-2" style={{ opacity: isRgbActive ? 1 : 0.4 }}>
            <Typography className="mb-1 font-bold">Hue - {hue}°</Typography>
            <div className="relative" style={{ height: 36 }}>
              <div
                className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none rounded-2xl"
                style={{
                  background:
                    "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
                }}
              />
              <Slider
                value={hue}
                onChange={handleHueChange}
                onChangeCommitted={handleHueChangeCommitted}
                min={0}
                max={360}
                step={1}
                disabled={disabled}
                sx={sliderStyles}
              />
            </div>
          </div>
        )}

        {showSaturation && (
          <div className="mb-2" style={{ opacity: isRgbActive ? 1 : 0.4 }}>
            <Typography className="mb-1 font-bold">
              Saturation - {saturation}%
            </Typography>
            <div className="relative" style={{ height: 36 }}>
              <div
                className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none rounded-2xl"
                style={{
                  background: `linear-gradient(to right, hsl(${hue}, 0%, 100%) 0%, hsl(${hue}, 100%, 50%) 100%)`,
                }}
              />
              <Slider
                value={saturation}
                onChange={handleSaturationChange}
                onChangeCommitted={handleSaturationChangeCommitted}
                min={0}
                max={100}
                step={1}
                disabled={disabled}
                sx={sliderStyles}
              />
            </div>
          </div>
        )}

        {showTemperature && (
          <div style={{ opacity: isTempActive ? 1 : 0.4 }}>
            <Typography className="mb-1 font-bold">
              Color Temperature - {colorTemp}K
            </Typography>
            <div className="relative" style={{ height: 36 }}>
              <div
                className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none rounded-2xl"
                style={{
                  background: `linear-gradient(to right, ${gradientStops.join(
                    ", "
                  )})`,
                }}
              />
              <Slider
                value={colorTemp}
                onChange={handleTemperatureChange}
                onChangeCommitted={handleTemperatureChangeCommitted}
                min={tempMinMax.min}
                max={tempMinMax.max}
                step={50}
                disabled={disabled}
                sx={sliderStyles}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorDetail1Widget;
